import { Button } from "src/apps/components/button/Button";
import { T } from "src/apps/components/i18n/Translation";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Input } from "src/apps/components/inputs/Input";
import { Text, BoldHeader1, SubText } from "src/apps/components/typography/Texts";
import { useTranslations } from "src/apps/hooks/useTranslations";
import styled, { useTheme } from "styled-components";
import { useSendTwoFactor } from "src/apps/hooks/useSendTwoFactor";
import { useAuth } from "src/apps/hooks/useAuth";
import { FadeInAnimation } from "src/apps/components/fadeIn/FadeInAnimation";
import { Avatar } from "src/apps/components/avatar/Avatar";
import { FormContainer } from "./FormContainer";
import { useGetUserLink } from "src/apps/hooks/useGetUserLink";
import { LoadingScreen } from "src/apps/components/loading/LoadingScreen";
import { useEffect, useState } from "react";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";

import { colors } from "@permisso/permisso-components";

const EMAIL_INPUT_PLACEHOLDER = "common.email.placeholder";
const EMAIL_INPUT_TITLE = "common.email.title";
const EMAIL_INPUT_ERROR = "common.error.invalid_email";
const LOGIN_SCREEN_TITLE = "documentation_area.login.email.title";
const LOGIN_SCREEN_SUBTITLE = "documentation_area.login.email.subtitle";
const FOOTER_TEXT = "documentation_area.login.footer";
const LOGIN_BUTTON_TEXT = "documentation_area.login.button";

export const LoginForm = () => {
  const { userLink, isGetUserLinkLoading } = useGetUserLink(); // TODO: handle useGetUserLink error
  const { mutate: sendTwoFactor, isPending } = useSendTwoFactor();
  const { t } = useTranslations();
  const { loginEmail, setLoginEmail, emailSubmitError, setEmailSubmitError } = useAuth();
  const { companyName, removeCustomerLogo } = useTheme();
  const [isEmailInputBlocked, setIsEmailInputBlocked] = useState(false);
  const { sendEvent } = useSendAnalyticEvent();

  useEffect(() => {
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_LOGIN_SCREEN_OPENED);
  }, []);

  useEffect(() => {
    if (userLink === undefined || userLink === null) {
      return;
    }

    if (userLink.email == "") {
      return;
    }

    setLoginEmail(userLink.email);
    setIsEmailInputBlocked(true);
  }, [userLink]);

  const isEmailValid = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const submit = async () => {
    const valid = isEmailValid(loginEmail);
    if (!valid) {
      setEmailSubmitError(EMAIL_INPUT_ERROR);
      return;
    }
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_LOGIN_SCREEN_CORRECT_EMAIL_ENTERED);
    sendTwoFactor(loginEmail);
  };

  const onEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailSubmitError("");
    setLoginEmail(event.target.value);
  };

  if (userLink === undefined || isGetUserLinkLoading) {
    return <LoadingScreen />;
  }

  return (
    <FadeInAnimation duration={0.7}>
      <FormContainer>
        {!removeCustomerLogo ? <Avatar /> : null}
        <BoldHeader1>
          <T k={LOGIN_SCREEN_TITLE} params={{ companyName }} />
        </BoldHeader1>
        <Text>
          <T k={LOGIN_SCREEN_SUBTITLE} />
        </Text>
        <Input
          value={loginEmail}
          onChange={onEmailInputChange}
          title={t(EMAIL_INPUT_TITLE)}
          placeholder={t(EMAIL_INPUT_PLACEHOLDER)}
          error={t(emailSubmitError)}
          rightIconSVGPath={ICON_PATHS.ENVELOPE}
          blocked={isEmailInputBlocked}
        />
        <Button
          text={t(LOGIN_BUTTON_TEXT)}
          onClick={submit}
          isLoading={isPending}
          disabled={!isEmailValid(loginEmail) || loginEmail === ""}
        />
        <FooterText>
          <T k={FOOTER_TEXT} />
        </FooterText>
      </FormContainer>
    </FadeInAnimation>
  );
};

const FooterText = styled(SubText)`
  text-align: center;
  color: ${colors.grey700};
`;
